import { CSSProperties, Dispatch, SetStateAction } from "react";
import { SelectPicker } from "rsuite";
import { findWhere } from "underscore";
import { LayoutAndSections, Module } from "../../../types/TenantInterfaces";

// For selecting a layout within the ViewRecordDrawer
export const ModuleLayoutSelector = ({
  module,
  layout,
  setLayout,
  style,
  block,
  className,
  onClear,
  hideOnOne = false, // Hide this selector when there is only one layout for the module.
  disabled = false,
}: {
  module: Module;
  layout: LayoutAndSections | null;
  setLayout: Dispatch<SetStateAction<null | LayoutAndSections>>;
  style?: CSSProperties;
  onClear?: () => void;
  block?: boolean;
  className?: string;
  hideOnOne?: boolean;
  disabled?: boolean;
}) => {
  const modLayouts = module?.Templates?.RecordDetailView?.Layouts;
  const layoutsList = Array.isArray(modLayouts) ? modLayouts : [];

  if (hideOnOne && layoutsList?.length < 2) return null;

  return (
    <>
      <SelectPicker
        disabled={disabled}
        labelKey="Name"
        valueKey="id"
        preventOverflow
        block={block}
        style={style}
        cleanable={!!onClear}
        size="sm"
        placeholder="Select a Layout"
        data={layoutsList}
        value={layout?.id}
        searchable={layoutsList.length > 5}
        onClean={onClear}
        onChange={(lID) => {
          const matchedLayout = GetSelectedLayout(module, lID as string);

          if (matchedLayout) setLayout(matchedLayout);
        }}
        className={className || "py-3"}
      />
    </>
  );
};

// If the schema specifies a dynamic layout field,
// locate the appropriate layout by examining the record's value associated with the dynamicLayoutField.
function GetDynamicLayout(module: Module, record: any) {
  const dynamicLayoutField =
    module?.Templates?.RecordDetailView?.DynamicLayoutField;

  if (!dynamicLayoutField) return undefined;

  return module.Templates.RecordDetailView?.Layouts?.find(
    (l) => l?.DynamicLayoutAssignment?.Value === record?.[dynamicLayoutField]
  );
}

export function GetSelectedLayout(module: Module, layoutID: string) {
  return findWhere(module?.Templates?.RecordDetailView?.Layouts || [], {
    id: layoutID ?? "",
  });
}

// Layout Precedence
// 1 - Dynamic Record Layout - Match record value to a layout
// 2 - The Layout matched with the DefaultLayoutID provided
// 3 - The Default Layout
export function GetDefaultLayout(
  module: Module,
  defaultLayoutID?: string,
  record?: any
) {
  let recordLayout;

  if (record) {
    recordLayout = GetDynamicLayout(module, record);
  }

  if (recordLayout) return recordLayout;

  if (defaultLayoutID) {
    recordLayout = GetSelectedLayout(module, defaultLayoutID);
  }

  if (recordLayout) return recordLayout;

  recordLayout = findWhere(module?.Templates?.RecordDetailView?.Layouts || [], {
    Default: true,
  });

  if (!recordLayout) {
    return {
      id: "Empty",
      Name: "Default",
      Default: true,
      Description: "",
      CoverImage: "",
      Sections: [],
    };
  }

  return recordLayout;
}
